import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckCircle, faExclamationTriangle, faPaperPlane, faBellSlash } from "@fortawesome/free-solid-svg-icons";
import ModalHeader from "./ModalHeader";
import api from "../services/api";
import styles from "./SendPushModal.module.css";
import { useBodyScrollLock } from "../hooks/useBodyScrollLock";

const MAX_LEN = 500;

export default function SendPushModal({
  open, onClose, studentId, studentName, defaultMessage = "",
  // Admin (AdminSendPushModal): envia em nome do TreinoNexo por outro endpoint
  endpoint = null,
  subtitle = null,
}) {
  useBodyScrollLock(open);

  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState(null);
  // "ok" = entregue de verdade; "warning" = o professor achou que avisou o
  // aluno, mas ninguém recebeu (sem push ativo, ou falha temporária) — usado
  // pra decidir cor/ícone do feedback sem re-adivinhar pelo texto da mensagem.
  const [successKind, setSuccessKind] = useState(null);
  const [error, setError] = useState(null);
  const [confirming, setConfirming] = useState(false);

  // defaultMessage: usado pelo atalho "Cutucar" da lista de alunos, que abre
  // o modal já com um texto plausível pro caso (aluno sumido / que nunca
  // treinou). Continua 100% editável — a ideia é poupar digitação, não
  // colocar palavra na boca do professor.
  useEffect(() => {
    if (open) {
      setMessage(defaultMessage || "");
      setSending(false);
      setSuccess(null);
      setSuccessKind(null);
      setError(null);
      setConfirming(false);
    }
  }, [open, defaultMessage]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!message.trim()) return;
    setError(null);
    setSuccess(null);
    setConfirming(true);
  };

  const handleConfirmSend = async () => {
    setSending(true);
    setError(null);
    setSuccess(null);
    setSuccessKind(null);
    try {
      const res = await api.post(endpoint || `/push/message-student/${studentId}`, {
        message: message.trim(),
      });
      const { sent = 0, devices = 0 } = res.data || {};
      if (devices === 0) {
        setSuccess(`${studentName || "O aluno"} ainda não ativou notificações no app - a mensagem não foi entregue.`);
        setSuccessKind("warning");
      } else if (sent === 0) {
        setSuccess(`Não foi possível entregar agora, mas ${devices} dispositivo(s) estão inscritos - pode ter sido um problema temporário.`);
        setSuccessKind("warning");
      } else {
        setSuccess(`Notificação enviada para ${sent} dispositivo(s) de ${studentName || "o aluno"}.`);
        setSuccessKind("ok");
      }
      setMessage("");
      window.setTimeout(() => { onClose(); }, 2000);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "object" ? detail.message : detail || "Erro ao enviar notificação.");
    } finally {
      setSending(false);
      setConfirming(false);
    }
  };

  if (!open) return null;

  return (
    <div className={styles.overlay} role="dialog" aria-modal="true">
      <div className={styles.modal}>
        <ModalHeader onClose={onClose} disabled={sending} />

        <div className={styles.header}>
          <p className={styles.title}>Enviar notificação</p>
          <p className={styles.sub}>
            {subtitle || `Mande um aviso avulso, na hora, direto pro celular de ${studentName || "este aluno"}`}
          </p>
        </div>

        <div className={styles.body}>
          {confirming && (
            <div className={styles.confirmBox}>
              <p className={styles.confirmMsg}>
                Enviar esta notificação para <strong>{studentName || "o aluno"}</strong> agora?
              </p>
              <div className={styles.confirmActions}>
                <button type="button" className={styles.confirmYes} onClick={handleConfirmSend} disabled={sending}>
                  {sending ? "Enviando..." : "Sim, enviar"}
                </button>
                <button type="button" className={styles.confirmNo} onClick={() => setConfirming(false)} disabled={sending}>
                  Cancelar
                </button>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className={styles.form}>
            <div className={styles.field}>
              <label htmlFor="push-message" className={styles.label}>Mensagem</label>
              <textarea
                id="push-message"
                className={styles.textarea}
                placeholder="Ex.: Não vi você treinar essa semana, tudo bem? Bora retomar!"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                maxLength={MAX_LEN}
                required
              />
              <span className={styles.charCount}>{message.length}/{MAX_LEN}</span>
            </div>

            {success && (
              <div className={successKind === "warning" ? styles.feedbackWarning : styles.feedbackSuccess}>
                <FontAwesomeIcon icon={successKind === "warning" ? faBellSlash : faCheckCircle} /> {success}
              </div>
            )}
            {error && (
              <div className={styles.feedbackError}>
                <FontAwesomeIcon icon={faExclamationTriangle} /> {error}
              </div>
            )}

            <button
              type="submit"
              className={styles.sendBtn}
              disabled={sending || !message.trim()}
            >
              <FontAwesomeIcon icon={faPaperPlane} />
              Enviar notificação
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
